import React from "react";
import { AlertCircle, CheckCircle, Loader2, WifiOff, Wifi } from "lucide-react";
import { PassportReaderStatus as Status } from "../../services/passportReader/passportReaderService";

interface PassportReaderStatusProps {
  status: Status;
  error?: string | null;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export const PassportReaderStatusIndicator: React.FC<
  PassportReaderStatusProps
> = ({ status, error, onConnect, onDisconnect }) => {
  const getStatusIcon = () => {
    switch (status) {
      case Status.DISCONNECTED:
        return <WifiOff className="h-5 w-5 text-slate-400" />;
      case Status.CONNECTING:
      case Status.INITIALIZING:
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case Status.CONNECTED:
        return <Wifi className="h-5 w-5 text-yellow-500" />;
      case Status.READY:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case Status.ERROR:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case Status.READING:
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <WifiOff className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case Status.DISCONNECTED:
        return "Passport Reader Disconnected";
      case Status.CONNECTING:
        return "Connecting to Passport Reader...";
      case Status.CONNECTED:
        return "Connected, Initializing...";
      case Status.INITIALIZING:
        return "Initializing Passport Reader...";
      case Status.READY:
        return "Passport Reader Ready";
      case Status.ERROR:
        return error || "Passport Reader Error";
      case Status.READING:
        return "Reading Passport...";
      default:
        return "Unknown Status";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case Status.DISCONNECTED:
        return "bg-slate-700 border-slate-600";
      case Status.CONNECTING:
      case Status.INITIALIZING:
      case Status.READING:
        return "bg-blue-900/20 border-blue-600";
      case Status.CONNECTED:
        return "bg-yellow-900/20 border-yellow-600";
      case Status.READY:
        return "bg-green-900/20 border-green-600";
      case Status.ERROR:
        return "bg-red-900/20 border-red-600";
      default:
        return "bg-slate-700 border-slate-600";
    }
  };

  const showConnectButton =
    status === Status.DISCONNECTED || status === Status.ERROR;
  const showDisconnectButton = status !== Status.DISCONNECTED;

  return (
    <div>
      <div
        className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor()}`}
      >
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div className="flex-1">
            <p className="text-sm font-medium text-white">{getStatusText()}</p>
          </div>
        </div>

        <div className="flex space-x-2">
          {showConnectButton && onConnect && (
            <button
              onClick={onConnect}
              className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {status === Status.ERROR ? "Retry" : "Connect"}
            </button>
          )}

          {showDisconnectButton && onDisconnect && (
            <button
              onClick={onDisconnect}
              className="px-3 py-1 text-xs font-medium bg-slate-600 text-white rounded hover:bg-slate-700 transition-colors"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      {status === Status.ERROR && error && (
        <div className="mt-2 p-3 bg-red-900/20 border border-red-600 rounded-lg">
          <p className="text-xs text-red-400 whitespace-pre-line">{error}</p>
          {error.includes("service is not available") && (
            <div className="mt-2 p-2 bg-slate-800 rounded border border-slate-700">
              <p className="text-xs text-slate-300 font-medium mb-1">
                Troubleshooting:
              </p>
              <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
                <li>Ensure the passport reader service is installed</li>
                <li>{`Start the service if it's not running`}</li>
                <li>{`Verify it's listening on port 90`}</li>
                <li>Check Windows Firewall or antivirus settings</li>
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
